/**
 * InOut Hooks — the person's record of residence absences, their clocks, plans
 * and notices (InOut plan §8). Always the caller's own record: no hook takes a
 * talent or journey id. Every number is the engine's; a component renders it.
 *
 * A write invalidates the whole InOut cache: the clocks, the plans and the
 * notices all read the same record.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DOMAIN_ERROR_CODES } from '@tonnie/foundation';
import type { InOutCaseView } from '@tonnie/types';
import type {
  InOutAvailabilityQuery,
  InOutConsentGrantInput,
  InOutConsultationCheckoutInput,
  InOutConsultationSlotInput,
  InOutRefundRequestInput,
  InOutRequisitionCheckoutInput,
  InOutCaseEventInput,
  InOutTrackingFileInput,
  InOutAbsenceCreateInput,
  InOutAbsenceJoinInput,
  InOutAbsenceUpdateInput,
  InOutCalculatorEvaluateInput,
  InOutCalculatorSimulateInput,
  InOutKeptCalculationInput,
  InOutHandoffCreateInput,
  InOutPortalSettingsInput,
  InOutConfirmationAnswerInput,
  InOutEvidenceAttachInput,
  InOutNoticePreferencesInput,
  InOutPlannedTripCreateInput,
  InOutPlannedTripUpdateInput,
  InOutSimulateInput,
  InOutTitleCreateInput,
  InOutTitleUpdateInput,
} from '@tonnie/schemas';

import { TonnieApiError } from '../client/api-error';
import { useSdkContext } from '../providers/SdkProvider';
import { createInOutService } from '../services/inout.service';
import { homeKeys } from './home-keys';
import { languageAcademyKeys } from './useLanguageAcademy';

export const inoutKeys = {
  all: ['inout'] as const,
  status: () => [...inoutKeys.all, 'status'] as const,
  record: () => [...inoutKeys.all, 'record'] as const,
  /** The history — every InOut; a detail sits under it, so reading the list again reads them too. */
  records: () => [...inoutKeys.all, 'records'] as const,
  recordDetail: (id: string) => [...inoutKeys.records(), id] as const,
  embedPortal: (code: string) => [...inoutKeys.all, 'embed-portal', code] as const,
  handoff: (token: string) => [...inoutKeys.all, 'handoff', token] as const,
  titles: () => [...inoutKeys.all, 'titles'] as const,
  absences: () => [...inoutKeys.all, 'absences'] as const,
  plannedTrips: () => [...inoutKeys.all, 'planned-trips'] as const,
  evidenceVocabulary: () => [...inoutKeys.all, 'evidence-vocabulary'] as const,
  noticePreferences: () => [...inoutKeys.all, 'notice-preferences'] as const,
  confirmations: () => [...inoutKeys.all, 'confirmations'] as const,
  calculatorRegimes: (countryCode: string) =>
    [...inoutKeys.all, 'calculator-regimes', countryCode] as const,
  agency: () => [...inoutKeys.all, 'agency'] as const,
  consents: () => [...inoutKeys.all, 'consents'] as const,
  availabilities: () => [...inoutKeys.all, 'availability'] as const,
  availability: (from: string, to: string) => [...inoutKeys.availabilities(), from, to] as const,
  cases: () => [...inoutKeys.all, 'cases'] as const,
  operator: () => [...inoutKeys.all, 'operator'] as const,
  operatorCases: () => [...inoutKeys.operator(), 'cases'] as const,
  operatorCase: (id: string) => [...inoutKeys.operator(), 'case', id] as const,
  trackingVocabulary: (agencyCode: string) =>
    [...inoutKeys.operator(), 'vocabulary', agencyCode] as const,
  trackingFiles: () => [...inoutKeys.operator(), 'files'] as const,
  portals: () => [...inoutKeys.operator(), 'portals'] as const,
  partners: () => [...inoutKeys.operator(), 'partners'] as const,
  reconciliation: (month: string) => [...inoutKeys.operator(), 'reconciliation', month] as const,
};

/**
 * Read again everything InOut has read and is on screen (pull to refresh):
 * the status, the record's lists, the agency — settled once they are back.
 */
export function useRefreshInOut() {
  const queryClient = useQueryClient();
  return () => queryClient.refetchQueries({ queryKey: inoutKeys.all, type: 'active' });
}

function useInOutService() {
  const { client } = useSdkContext();
  return createInOutService(client);
}

type InOutKey = readonly unknown[];

/**
 * The whole record — what a calculation, a pause or a history action (a new
 * InOut, a restore, a close, a delete) changes: every InOut read, the history
 * included; never the operation's reads.
 */
const WHOLE_RECORD: readonly InOutKey[] = [
  inoutKeys.status(),
  inoutKeys.record(),
  inoutKeys.records(),
  inoutKeys.titles(),
  inoutKeys.absences(),
  inoutKeys.plannedTrips(),
  inoutKeys.confirmations(),
  inoutKeys.noticePreferences(),
  inoutKeys.agency(),
];

/**
 * A refusal that says the screen is behind the record — paused on another
 * device, gone, overlapping, already answered, taken: what the write would
 * have changed is read again, with the record's state, so the next touch is
 * made on what is there.
 */
const STALE_REFUSALS: ReadonlySet<string> = new Set([
  DOMAIN_ERROR_CODES.INOUT_DEACTIVATED,
  DOMAIN_ERROR_CODES.INOUT_NOT_SET_UP,
  DOMAIN_ERROR_CODES.INOUT_ITEM_NOT_FOUND,
  DOMAIN_ERROR_CODES.VAULT_FILE_NOT_LINKABLE,
  DOMAIN_ERROR_CODES.INOUT_ABSENCE_OVERLAP,
  DOMAIN_ERROR_CODES.INOUT_CONFIRMATION_CLOSED,
  DOMAIN_ERROR_CODES.INOUT_CASE_OPEN,
  DOMAIN_ERROR_CODES.INOUT_RECORD_IN_USE,
  DOMAIN_ERROR_CODES.INOUT_SLOT_UNAVAILABLE,
  DOMAIN_ERROR_CODES.INOUT_REQUISITION_EXISTS,
  DOMAIN_ERROR_CODES.INOUT_CALENDAR_AVAILABLE,
]);

/**
 * A write, then the reads it changes read again — `reads`, never every InOut
 * read (the agency's calendar, a portal, a handoff or the operation's lists
 * are not the record). `alsoHome`: the write moves a card on Home ("Needs
 * you"), read again too. A refusal that says the screen is behind reads
 * the same again, with the record's state and Home.
 */
function useInOutWrite<TVariables, TResult>(
  write: (variables: TVariables) => Promise<TResult>,
  options: { reads: readonly InOutKey[]; alsoHome?: boolean }
) {
  const queryClient = useQueryClient();
  const readAgain = (keys: readonly InOutKey[], home: boolean) =>
    Promise.all([
      ...keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      home ? queryClient.invalidateQueries({ queryKey: homeKeys.all }) : undefined,
    ]);
  return useMutation({
    mutationFn: write,
    onSuccess: () => readAgain(options.reads, options.alsoHome === true),
    onError: (error) =>
      error instanceof TonnieApiError && error.code !== undefined && STALE_REFUSALS.has(error.code)
        ? readAgain(
            [...options.reads, inoutKeys.status(), inoutKeys.record(), inoutKeys.records()],
            true
          )
        : undefined,
  });
}

/** A planned trip moves the projection, the record's holds and Home's "limit close" card. */
const TRIP_READS = {
  reads: [inoutKeys.plannedTrips(), inoutKeys.status(), inoutKeys.record()],
  alsoHome: true,
};

/** A consultation bought or moved: the cases, the agency's answer and the open times. */
const CASE_READS = {
  reads: [inoutKeys.cases(), inoutKeys.agency(), inoutKeys.availabilities()],
};

// ── Reads ──

export function useInOutStatus(options?: { enabled?: boolean }) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.status(),
    queryFn: () => service.getStatus(),
    enabled: options?.enabled ?? true,
  });
}

/** An active portal as its calculator reads it (before sign-up). */
/** The portal is unknown or not active — the API's answer, not a failure to reach it. */
export function isInOutPortalUnavailable(error: unknown): boolean {
  return (
    error instanceof TonnieApiError && error.code === DOMAIN_ERROR_CODES.INOUT_PORTAL_UNAVAILABLE
  );
}

/** The hand-off link was used or is past its 72 hours — the API's answer, not a failure to reach it. */
export function isInOutHandoffGone(error: unknown): boolean {
  return (
    error instanceof TonnieApiError && error.code === DOMAIN_ERROR_CODES.INOUT_HANDOFF_NOT_FOUND
  );
}

/** Retries left for a read that failed to reach the API — never for its own answer. */
const READ_RETRIES = 2;

export function useInOutEmbedPortal(code: string | null) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.embedPortal(code ?? ''),
    queryFn: () => service.getEmbedPortal(code ?? ''),
    enabled: code !== null,
    staleTime: 5 * 60_000,
    // A portal that is not there is said at once; a blip is tried again.
    retry: (failures, error) => !isInOutPortalUnavailable(error) && failures < READ_RETRIES,
  });
}

/** Keeping the answer on a portal: the calculation handed to Tonnie. */
export function useCreateInOutHandoff() {
  const service = useInOutService();
  return useMutation({
    mutationFn: (input: InOutHandoffCreateInput) => service.createHandoff(input),
  });
}

/** What sign-up says back about a handed-off calculation — null token, nothing read. */
export function useInOutHandoffSummary(token: string | null) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.handoff(token ?? ''),
    queryFn: () => service.getHandoffSummary(token ?? ''),
    enabled: token !== null,
    // A link that is gone is said at once; a blip is tried again.
    retry: (failures, error) => !isInOutHandoffGone(error) && failures < READ_RETRIES,
  });
}

/** Signed in: the handed-off calculation becomes the record. */
export function useAdoptInOutHandoff() {
  const service = useInOutService();
  return useInOutWrite((token: string) => service.adoptHandoff(token), {
    reads: WHOLE_RECORD,
    alsoHome: true,
  });
}

/** An InOut already there: a new one from the handed-off calculation — the one there goes to the history. */
export function useReplaceInOutWithHandoff() {
  const service = useInOutService();
  return useInOutWrite((token: string) => service.replaceWithHandoff(token), {
    reads: WHOLE_RECORD,
    alsoHome: true,
  });
}

/** The InOut as a whole — none | active | deactivated — read without the engine (the app's tab). */
export function useInOutRecord(options?: { enabled?: boolean }) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.record(),
    queryFn: () => service.getRecord(),
    enabled: options?.enabled ?? true,
  });
}

export function useInOutTitles(options?: { enabled?: boolean }) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.titles(),
    queryFn: () => service.listTitles(),
    enabled: options?.enabled ?? true,
  });
}

export function useInOutAbsences(options?: { enabled?: boolean }) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.absences(),
    queryFn: () => service.listAbsences(),
    enabled: options?.enabled ?? true,
  });
}

export function useInOutPlannedTrips(options?: { enabled?: boolean }) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.plannedTrips(),
    queryFn: () => service.listPlannedTrips(),
    enabled: options?.enabled ?? true,
  });
}

/** What the record's country accepts as proof of a trip. */
export function useInOutEvidenceVocabulary(options?: { enabled?: boolean }) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.evidenceVocabulary(),
    queryFn: () => service.getEvidenceVocabulary(),
    enabled: options?.enabled ?? true,
  });
}

export function useInOutNoticePreferences(options?: { enabled?: boolean }) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.noticePreferences(),
    queryFn: () => service.getNoticePreferences(),
    enabled: options?.enabled ?? true,
  });
}

export function useInOutConfirmations(options?: { enabled?: boolean }) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.confirmations(),
    queryFn: () => service.listConfirmations(),
    enabled: options?.enabled ?? true,
  });
}

/** The public calculator's regimes — no account needed. */
export function useInOutCalculatorRegimes(countryCode: string) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.calculatorRegimes(countryCode),
    queryFn: () => service.calculatorRegimes(countryCode),
    enabled: !!countryCode,
    retry: false,
  });
}

// ── Writes ──

/** No dates: the In/Out switch. */
export function useCreateInOutAbsence() {
  const service = useInOutService();
  return useInOutWrite((input: InOutAbsenceCreateInput) => service.createAbsence(input), {
    reads: [inoutKeys.absences(), inoutKeys.plannedTrips(), inoutKeys.status(), inoutKeys.record()],
    alsoHome: true,
  });
}

export function useUpdateInOutAbsence() {
  const service = useInOutService();
  return useInOutWrite(
    ({ id, input }: { id: string; input: InOutAbsenceUpdateInput }) =>
      service.updateAbsence(id, input),
    {
      reads: [
        inoutKeys.absences(),
        inoutKeys.plannedTrips(),
        inoutKeys.status(),
        inoutKeys.record(),
      ],
      alsoHome: true,
    }
  );
}

/** The switch's second touch: back today — the server says what day that is. */
export function useReturnFromInOutAbsence() {
  const service = useInOutService();
  return useInOutWrite((id: string) => service.returnFromAbsence(id), {
    reads: [inoutKeys.absences(), inoutKeys.plannedTrips(), inoutKeys.status(), inoutKeys.record()],
    alsoHome: true,
  });
}

/** Two trips that share only a border day, made one — the day counts once (22 Oct 2026). */
export function useJoinInOutAbsence() {
  const service = useInOutService();
  return useInOutWrite(
    ({ id, input }: { id: string; input: InOutAbsenceJoinInput }) => service.joinAbsence(id, input),
    {
      reads: [inoutKeys.absences(), inoutKeys.status(), inoutKeys.record()],
      alsoHome: true,
    }
  );
}

export function useRemoveInOutAbsence() {
  const service = useInOutService();
  return useInOutWrite((id: string) => service.removeAbsence(id), {
    reads: [inoutKeys.absences(), inoutKeys.plannedTrips(), inoutKeys.status(), inoutKeys.record()],
    alsoHome: true,
  });
}

export function useAttachInOutEvidence() {
  const service = useInOutService();
  return useInOutWrite(
    ({ absenceId, input }: { absenceId: string; input: InOutEvidenceAttachInput }) =>
      service.attachEvidence(absenceId, input),
    { reads: [inoutKeys.absences(), inoutKeys.record()] }
  );
}

/** A boarding pass read into the trip it is — a read: nothing is written, nothing is read again. */
export function useReadInOutBoardingPass() {
  const service = useInOutService();
  return useMutation({
    mutationFn: (vaultFileId: string) => service.readBoardingPass(vaultFileId),
  });
}

export function useDetachInOutEvidence() {
  const service = useInOutService();
  return useInOutWrite(
    ({ absenceId, evidenceId }: { absenceId: string; evidenceId: string }) =>
      service.detachEvidence(absenceId, evidenceId),
    { reads: [inoutKeys.absences(), inoutKeys.record()] }
  );
}

/** Pause the InOut — its questions leave Home, so Home is read again. */
export function useDeactivateInOut() {
  const service = useInOutService();
  return useInOutWrite(() => service.deactivateRecord(), { reads: WHOLE_RECORD, alsoHome: true });
}

export function useReactivateInOut() {
  const service = useInOutService();
  return useInOutWrite(() => service.reactivateRecord(), { reads: WHOLE_RECORD, alsoHome: true });
}

/** A title the person holds now — a renewed card, chained to the one it renews. */
export function useCreateInOutTitle() {
  const service = useInOutService();
  return useInOutWrite((input: InOutTitleCreateInput) => service.createTitle(input), {
    reads: [inoutKeys.titles(), inoutKeys.status(), inoutKeys.record()],
    alsoHome: true,
  });
}

/** A correction to a title — its country never changes. */
export function useUpdateInOutTitle() {
  const service = useInOutService();
  return useInOutWrite(
    ({ id, input }: { id: string; input: InOutTitleUpdateInput }) => service.updateTitle(id, input),
    { reads: [inoutKeys.titles(), inoutKeys.status(), inoutKeys.record()], alsoHome: true }
  );
}

/** The person's InOuts — the principal first, then the closed ones (the history). */
export function useInOutRecords(options?: { enabled?: boolean }) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.records(),
    queryFn: () => service.listRecords(),
    enabled: options?.enabled ?? true,
  });
}

/** One InOut read whole — null id, nothing read. */
export function useInOutRecordDetail(id: string | null) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.recordDetail(id ?? ''),
    queryFn: () => service.getRecordDetail(id ?? ''),
    enabled: id !== null,
  });
}

/** A new InOut from a calculation — the principal, if any, goes to the history; nothing is deleted. */
export function useNewInOutRecord() {
  const service = useInOutService();
  return useInOutWrite((input: InOutKeptCalculationInput) => service.newRecord(input), {
    reads: WHOLE_RECORD,
    alsoHome: true,
  });
}

/** A closed InOut back as the principal — paused (H1); the one running goes to the history. */
export function useRestoreInOutRecord() {
  const service = useInOutService();
  return useInOutWrite((id: string) => service.restoreRecord(id), {
    reads: WHOLE_RECORD,
    alsoHome: true,
  });
}

/** The principal closed into the history, with no new one (H2). */
export function useCloseInOutRecord() {
  const service = useInOutService();
  return useInOutWrite(() => service.closeRecord(), { reads: WHOLE_RECORD, alsoHome: true });
}

/** A closed InOut deleted for good (H3) — refused while a case with the agency uses it. */
export function useDeleteInOutRecord() {
  const service = useInOutService();
  return useInOutWrite((id: string) => service.deleteRecord(id), {
    reads: WHOLE_RECORD,
    alsoHome: true,
  });
}

export function useAdoptInOutCalculation() {
  const service = useInOutService();
  return useInOutWrite((input: InOutKeptCalculationInput) => service.adoptKeptCalculation(input), {
    reads: WHOLE_RECORD,
    alsoHome: true,
  });
}

export function useCreateInOutPlannedTrip() {
  const service = useInOutService();
  return useInOutWrite(
    (input: InOutPlannedTripCreateInput) => service.createPlannedTrip(input),
    TRIP_READS
  );
}

export function useUpdateInOutPlannedTrip() {
  const service = useInOutService();
  return useInOutWrite(
    ({ id, input }: { id: string; input: InOutPlannedTripUpdateInput }) =>
      service.updatePlannedTrip(id, input),
    TRIP_READS
  );
}

export function useRemoveInOutPlannedTrip() {
  const service = useInOutService();
  return useInOutWrite((id: string) => service.removePlannedTrip(id), TRIP_READS);
}

export function usePutInOutNoticePreferences() {
  const service = useInOutService();
  return useInOutWrite(
    (input: InOutNoticePreferencesInput) => service.putNoticePreferences(input),
    // The lead time moves the "limit close" card on Home.
    { reads: [inoutKeys.noticePreferences()], alsoHome: true }
  );
}

export function useAnswerInOutConfirmation() {
  const service = useInOutService();
  return useInOutWrite(
    ({ id, input }: { id: string; input: InOutConfirmationAnswerInput }) =>
      service.answerConfirmation(id, input),
    // The open question is a card on Home.
    { reads: [inoutKeys.confirmations(), inoutKeys.status()], alsoHome: true }
  );
}

// ── Questions that write nothing ──

/** A trip simulated on the record — never saved. */
export function useSimulateInOutTrip() {
  const service = useInOutService();
  return useMutation({ mutationFn: (input: InOutSimulateInput) => service.simulate(input) });
}

/** The public calculator — the dashboard's engine on a record the person describes (AC-1). */
export function useInOutCalculatorEvaluate() {
  const service = useInOutService();
  return useMutation({
    mutationFn: (input: InOutCalculatorEvaluateInput) => service.calculatorEvaluate(input),
  });
}

/** "What if I travel?" on the calculator's record — nothing saved. */
export function useInOutCalculatorSimulate() {
  const service = useInOutService();
  return useMutation({
    mutationFn: (input: InOutCalculatorSimulateInput) => service.calculatorSimulate(input),
  });
}

// ── The dossier ──

/**
 * Download the agency's copy of a dossier, through its private link. A
 * mutation, never a query: the page must not fetch it on load — mail scanners
 * open links unattended, and every opening is recorded.
 */
export function useDownloadSharedDossier() {
  const service = useInOutService();
  return useMutation({ mutationFn: (token: string) => service.getSharedDossier(token) });
}

// ── The operation (Tonnie staff) ──
// Each read is recorded server-side (AC-21): no background refetching.
const OPERATOR_READ = { refetchOnWindowFocus: false, staleTime: 60_000 } as const;

export function useInOutOperatorCases(options?: { enabled?: boolean }) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.operatorCases(),
    queryFn: () => service.listOperatorCases(),
    enabled: options?.enabled ?? true,
    ...OPERATOR_READ,
  });
}

export function useInOutOperatorCase(id: string | null) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.operatorCase(id ?? ''),
    queryFn: () => service.getOperatorCase(id ?? ''),
    enabled: id !== null,
    ...OPERATOR_READ,
  });
}

/** A write on a case: the case and the list are read again. */
function useOperatorCaseWrite<TVariables, TResult>(
  write: (variables: TVariables) => Promise<TResult>
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: inoutKeys.operator() }),
  });
}

export function useRecordInOutCaseEvent(caseId: string) {
  const service = useInOutService();
  return useOperatorCaseWrite((input: InOutCaseEventInput) =>
    service.recordCaseEvent(caseId, input)
  );
}

export function useHandleInOutRefundRequest(caseId: string) {
  const service = useInOutService();
  return useOperatorCaseWrite((requestId: string) =>
    service.handleRefundRequest(caseId, requestId)
  );
}

export function useInOutTrackingVocabulary(agencyCode: string | null) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.trackingVocabulary(agencyCode ?? ''),
    queryFn: () => service.getTrackingVocabulary(agencyCode ?? ''),
    enabled: agencyCode !== null,
    ...OPERATOR_READ,
  });
}

export function useInOutTrackingFiles(options?: { enabled?: boolean }) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.trackingFiles(),
    queryFn: () => service.listTrackingFiles(),
    enabled: options?.enabled ?? true,
    ...OPERATOR_READ,
  });
}

export function usePreviewInOutTrackingFile() {
  const service = useInOutService();
  return useOperatorCaseWrite((input: InOutTrackingFileInput) =>
    service.previewTrackingFile(input)
  );
}

export function useCommitInOutTrackingFile() {
  const service = useInOutService();
  return useOperatorCaseWrite((id: string) => service.commitTrackingFile(id));
}

export function useInOutPortals(options?: { enabled?: boolean }) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.portals(),
    queryFn: () => service.listPortals(),
    enabled: options?.enabled ?? true,
    ...OPERATOR_READ,
  });
}

/** The partner registry — the agency a portal names is picked from it. */
export function useInOutOperatorPartners(options?: { enabled?: boolean }) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.partners(),
    queryFn: () => service.listOperatorPartners(),
    enabled: options?.enabled ?? true,
    ...OPERATOR_READ,
  });
}

export function useSaveInOutPortal() {
  const service = useInOutService();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ code, input }: { code: string; input: InOutPortalSettingsInput }) =>
      service.savePortal(code, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: inoutKeys.portals() }),
  });
}

export function useInOutReconciliation(month: string | null) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.reconciliation(month ?? ''),
    queryFn: () => service.getReconciliation(month ?? ''),
    enabled: month !== null,
    ...OPERATOR_READ,
  });
}

// ── The agency (Lane 8) ──

export function useInOutAgency(options?: { enabled?: boolean }) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.agency(),
    queryFn: () => service.getAgency(),
    enabled: options?.enabled ?? true,
  });
}

export function useDataSharingConsents(options?: { enabled?: boolean }) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.consents(),
    queryFn: () => service.listDataSharingConsents(),
    enabled: options?.enabled ?? true,
  });
}

export function useGrantInOutConsent() {
  const service = useInOutService();
  return useInOutWrite((input: InOutConsentGrantInput) => service.grantConsent(input), {
    reads: [inoutKeys.consents(), inoutKeys.agency()],
  });
}

/** Revoke any of the person's consents — what rests on it reads again (the agency, the Languages Academy). */
export function useRevokeDataSharingConsent() {
  const service = useInOutService();
  return useInOutWrite((id: string) => service.revokeDataSharingConsent(id), {
    reads: [inoutKeys.consents(), inoutKeys.agency(), languageAcademyKeys.all],
  });
}

/** The agency's open times between two instants — a week on screen. */
export function useInOutConsultationAvailability(range: InOutAvailabilityQuery | null) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.availability(range?.from ?? '', range?.to ?? ''),
    queryFn: () => service.getConsultationAvailability(range as InOutAvailabilityQuery),
    enabled: range !== null,
    // A calendar that cannot be read (503) is an answer, not a blip.
    retry: false,
  });
}

export function useRequestInOutContact() {
  const service = useInOutService();
  return useMutation({ mutationFn: () => service.requestContact() });
}

export function useInOutConsultationCheckout() {
  const service = useInOutService();
  return useInOutWrite(
    (input: InOutConsultationCheckoutInput) => service.checkoutConsultation(input),
    CASE_READS
  );
}

export function useMoveInOutConsultation() {
  const service = useInOutService();
  return useInOutWrite(
    (input: { id: string } & InOutConsultationSlotInput) =>
      service.moveConsultation(input.id, { slotStart: input.slotStart }),
    CASE_READS
  );
}

/** Back from Stripe, the cases are read again this often while a payment is being confirmed… */
const PAYMENT_POLL_MS = 3_000;
/** …for this many reads at most (two minutes): a webhook later than that is the daily read-back's. */
const PAYMENT_POLL_READS = 40;

/**
 * The person's agency cases. `pollWhile` (back from Stripe's page with a
 * success: "a payment is still pending") reads them again while it holds —
 * the webhook that records the payment lands a few seconds after the person
 * is back.
 */
export function useInOutCases(options?: {
  enabled?: boolean;
  pollWhile?: (cases: InOutCaseView[] | undefined) => boolean;
}) {
  const service = useInOutService();
  return useQuery({
    queryKey: inoutKeys.cases(),
    queryFn: () => service.listCases(),
    enabled: options?.enabled ?? true,
    refetchInterval: (query) =>
      options?.pollWhile?.(query.state.data) && query.state.dataUpdateCount < PAYMENT_POLL_READS
        ? PAYMENT_POLL_MS
        : false,
  });
}

export function useInOutRequisitionCheckout() {
  const service = useInOutService();
  return useInOutWrite(
    (input: InOutRequisitionCheckoutInput) => service.checkoutRequisition(input),
    { reads: [inoutKeys.cases()] }
  );
}

export function useRequestInOutRefund() {
  const service = useInOutService();
  return useInOutWrite(
    (input: { caseId: string } & InOutRefundRequestInput) =>
      service.requestRefund(input.caseId, { reasonCode: input.reasonCode }),
    { reads: [inoutKeys.cases()] }
  );
}

/** The person's own copy of their dossier — downloaded when asked, never on load. */
export function useDownloadInOutDossier() {
  const service = useInOutService();
  return useMutation({ mutationFn: () => service.getOwnDossier() });
}

/** The person's own record (PDF) — downloaded when asked, never on load. */
export function useDownloadInOutRecord() {
  const service = useInOutService();
  return useMutation({ mutationFn: () => service.getOwnRecord() });
}
